import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import MapPreview from './MapPreview';
import { MapPin, Navigation, Check, AlertCircle, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function AddressConfirmation({ 
  onConfirm, 
  initialAddress = '',
  initialLocation = null,
  required = true 
}) {
  const [address, setAddress] = useState(initialAddress);
  const [location, setLocation] = useState(initialLocation);
  const [loading, setLoading] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);

  // Get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setLocation({ lat, lng });
        
        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
          );
          const data = await response.json();
          if (data.display_name) {
            setAddress(data.display_name);
          }
        } catch (error) {
          console.error('Reverse geocoding failed:', error);
        }
        
        setGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        toast.error('Could not get your location. Please enter address manually.');
        setManualEntry(true);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Geocode address to coordinates
  const geocodeAddress = async () => {
    if (!address.trim()) {
      toast.error('Please enter an address');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        setLocation({ lat: parseFloat(result.lat), lng: parseFloat(result.lon) });
        setAddress(result.display_name);
        toast.success('Address found! Please confirm the location on the map.');
      } else {
        toast.error('Address not found. Please try a different address.');
      }
    } catch (error) {
      console.error('Geocoding failed:', error);
      toast.error('Failed to find address. Please try again.');
    }
    setLoading(false);
  };

  // Handle map pin drag
  const handleLocationChange = async (newLocation) => {
    setLocation(newLocation);
    setConfirmed(false);
    
    // Reverse geocode new location
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${newLocation.lat}&lon=${newLocation.lng}`
      );
      const data = await response.json();
      if (data.display_name) {
        setAddress(data.display_name);
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
    }
  };

  // Confirm the location
  const handleConfirm = () => {
    if (!location || !address) {
      toast.error('Please provide a valid address');
      return;
    }
    
    setConfirmed(true);
    onConfirm({
      address,
      location: {
        lat: location.lat,
        lng: location.lng,
        latitude: location.lat,
        longitude: location.lng
      }
    });
    toast.success('Address confirmed!');
  };

  // Auto-get location on mount
  useEffect(() => {
    if (!initialLocation && !initialAddress) {
      getCurrentLocation();
    }
  }, []);

  return (
    <Card className="border-blue-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPin className="w-5 h-5 text-blue-600" />
          Confirm Service Location
        </CardTitle>
        <CardDescription>
          Verify your exact location so the provider can find you
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Get Current Location Button */}
        {!location && !manualEntry && (
          <Button
            onClick={getCurrentLocation}
            disabled={gettingLocation}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {gettingLocation ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Getting your location...
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4 mr-2" />
                Use My Current Location
              </>
            )}
          </Button>
        )}

        {/* Manual Address Entry */}
        <div className="space-y-2">
          <Label htmlFor="address">Service Address</Label>
          <div className="flex gap-2">
            <Input
              id="address"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                setConfirmed(false);
              }}
              placeholder="Enter full address..."
              className="flex-1"
            />
            <Button
              onClick={geocodeAddress}
              disabled={loading || !address.trim()}
              variant="outline"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Include street number, street name, city, and state for best results
          </p>
        </div>

        {/* Map Preview */}
        {location && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Location Preview</Label>
              <p className="text-xs text-gray-500">Drag the pin to adjust if needed</p>
            </div>
            <MapPreview
              location={location}
              address={address}
              height="250px"
              draggable={true}
              onLocationChange={handleLocationChange}
              showNavigation={false}
              showCallButton={false}
            />
          </div>
        )}

        {/* Confirmation Status */}
        {confirmed ? (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-700 font-medium">Location Confirmed!</span>
          </div>
        ) : location ? (
          <div className="space-y-3">
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-yellow-700 font-medium">Please confirm this address</p>
                <p className="text-sm text-yellow-600">
                  Make sure the pin is at your exact location so the provider can find you easily.
                </p>
              </div>
            </div>
            
            <Button
              onClick={handleConfirm}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Check className="w-4 h-4 mr-2" />
              Confirm This Address
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => setManualEntry(true)}
            variant="outline"
            className="w-full"
          >
            Enter Address Manually
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
